import React from "react";
import { useLocation } from "react-router-dom";
import { Navbar } from "./Navbar";
import { Footer } from "./Footer";

export const ProdottiMarchi = () => {
  const { state: product } = useLocation();
  const [isImageZoomed, setIsImageZoomed] = React.useState(false);

  // Scroll to top when component mounts
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (!product) {
    return (
      <div className="flex flex-col items-center bg-gradient-to-b from-gray-50 to-white w-full min-h-screen">
        <Navbar />
        <p className="text-center text-red-500 font-elegant mt-12" role="alert">
          Nessun prodotto selezionato.
        </p>
        <Footer />
      </div>
    );
  }

  return (
    <div className="flex flex-col bg-gradient-to-b from-gray-50 to-white w-full font-elegant">
      <Navbar />

      <main className="w-full max-w-7xl mx-auto px-4 md:px-6 py-6 md:py-12">
        {/* Breadcrumb */}
        <nav className="mb-6 md:mb-8" aria-label="Breadcrumb">
          <ol className="flex items-center gap-2 text-sm text-gray-600">
            <li><a href="/" className="hover:text-green-800 transition-colors">Home</a></li>
            <li><span className="text-gray-400">/</span></li>
            <li><a href="/marchi" className="hover:text-green-800 transition-colors">Marchi</a></li>
            <li><span className="text-gray-400">/</span></li>
            <li className="text-gray-800 font-medium truncate max-w-[150px]">{product.name}</li>
          </ol>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12">
          {/* Image Section with Zoom */}
          <div className="space-y-4">
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden sticky top-24">
              <button
                className="aspect-square relative bg-gray-100 flex items-center justify-center p-8 w-full cursor-zoom-in focus:outline-none focus:ring-4 focus:ring-green-800"
                onClick={() => setIsImageZoomed(true)}
                aria-label="Clicca per ingrandire l'immagine"
              >
                <img
                  src={product.image}
                  alt={`${product.name} - ${product.material}`}
                  className="w-full h-full object-contain transition-transform duration-300 hover:scale-105"
                />
                <div className="absolute bottom-3 right-3 bg-black/60 text-white text-xs px-2 py-1 rounded-md flex items-center gap-1">
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v6m3-3H7" />
                  </svg>
                  Zoom
                </div>
              </button>
            </div>
            <p className="text-xs text-gray-500 text-center">Clicca sull'immagine per ingrandirla</p>
          </div>

          {/* Fullscreen Image Modal */}
          {isImageZoomed && (
            <div
              className="fixed inset-0 bg-black/95 z-[100] flex items-center justify-center p-4 cursor-zoom-out"
              onClick={() => setIsImageZoomed(false)}
              role="dialog"
              aria-label="Immagine ingrandita"
            >
              <button
                className="absolute top-4 right-4 text-white bg-black/50 hover:bg-black/70 rounded-full p-2 focus:outline-none focus:ring-4 focus:ring-white"
                onClick={() => setIsImageZoomed(false)}
                aria-label="Chiudi zoom"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              <img
                src={product.image}
                alt={`${product.name} - ${product.material} (ingrandita)`}
                className="max-w-full max-h-full object-contain"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          )}

          {/* Details Section */}
          <div className="space-y-6">
            <div>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-3">
                {product.name}
              </h1>
              <p className="text-lg md:text-xl text-gray-600">
                {product.marca}
              </p>
            </div>

            {/* Price */}
            <div className="bg-green-50 border-2 border-green-800 rounded-xl p-6">
              <p className="text-sm text-gray-600 mb-1">Prezzo</p>
              <p className="text-3xl md:text-4xl font-bold text-green-800">
                {product.price}
              </p>
            </div>

            {/* Product Info */}
            <div className="bg-white rounded-xl shadow-md p-6 space-y-4">
              <h2 className="text-xl font-bold text-gray-800 mb-4">Dettagli Prodotto</h2>

              <div className="flex items-start gap-3 pb-4 border-b border-gray-200">
                <svg className="w-5 h-5 text-green-800 mt-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                </svg>
                <div className="flex-1">
                  <p className="text-sm text-gray-600 mb-1">Marca</p>
                  <p className="text-base font-semibold text-gray-800">{product.marca}</p>
                </div>
              </div>

              <div className="flex items-start gap-3 pb-4 border-b border-gray-200">
                <svg className="w-5 h-5 text-green-800 mt-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                </svg>
                <div className="flex-1">
                  <p className="text-sm text-gray-600 mb-1">Materiale</p>
                  <p className="text-base font-semibold text-gray-800">{product.material}</p>
                </div>
              </div>

              {product.description && (
                <div className="pt-2">
                  <p className="text-sm text-gray-600 mb-2">Descrizione</p>
                  <p className="text-base text-gray-700 leading-relaxed">{product.description}</p>
                </div>
              )}
            </div>

            {/* Contact CTA */}
            <div className="bg-gradient-to-r from-green-800 to-green-900 rounded-xl shadow-lg p-6 md:p-8 text-white">
              <h3 className="text-xl md:text-2xl font-bold mb-3">Interessato a questo prodotto?</h3>
              <p className="text-sm md:text-base mb-6 opacity-90">
                Contattaci per maggiori informazioni, disponibilità e per fissare un appuntamento nel nostro negozio.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <a
                  href="tel:+39039388876"
                  className="flex items-center justify-center gap-2 bg-white text-green-800 hover:bg-gray-100 font-semibold py-3 px-6 rounded-lg transition-colors focus:outline-none focus:ring-4 focus:ring-white/50"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  Chiama Ora
                </a>
                <a
                  href="/contattaci"
                  className="flex items-center justify-center gap-2 bg-green-700 hover:bg-green-600 text-white font-semibold py-3 px-6 rounded-lg transition-colors focus:outline-none focus:ring-4 focus:ring-white/50"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  Invia Email
                </a>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};
